/**
 * Usage Service
 *
 * Provides real-time subscription to account usage data from RTDB.
 */

import { limitsRTDB } from '@/Firebase';
import { AccountUsage } from '@superfunnel/types';
import { onValue, ref, Unsubscribe } from 'firebase/database';

/**
 * Subscribe to real-time usage updates for an account.
 *
 * @param accountId - The account ID to subscribe to
 * @param callback - Called with the latest usage data
 * @returns Unsubscribe function to stop listening
 */
export function subscribeToUsage(
  accountId: string,
  callback: (usage: AccountUsage | null) => void,
): Unsubscribe {
  const usageRef = ref(limitsRTDB, `accounts/${accountId}/usage`);

  return onValue(
    usageRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      const data = snapshot.val();

      // Normalize the data to ensure all fields have defaults
      const usage: AccountUsage = {
        periodId: data.periodId || '',
        periodStart: data.periodStart || 0,
        periodEnd: data.periodEnd || 0,
        domains: data.domains || 0,
        pages: data.pages || 0,
        publishedPages: data.publishedPages || 0,
        members: data.members || 0,
        aiTokens: {
          inputTokens: data.aiTokens?.inputTokens || 0,
          outputTokens: data.aiTokens?.outputTokens || 0,
          total: data.aiTokens?.total || 0,
          spendTotal: data.aiTokens?.spendTotal || 0,
          byModel: data.aiTokens?.byModel || {},
          byFeature: data.aiTokens?.byFeature || {},
          byMember: data.aiTokens?.byMember || {},
        },
      };

      callback(usage);
    },
    (error) => {
      console.error('Error subscribing to usage:', error);
      callback(null);
    },
  );
}
